import { skipToken } from '@reduxjs/toolkit/query';
import { ReferentPriceTypes } from '@strata/constants';
import { conditionalString, formatDate } from '@strata/helper-functions';
import { DollarAmount, formatDollarAmount } from '@strata/react-dollar-amount';
import { FormSectionWithRows } from '@strata/react-form-section';
import {
    DetailLabel,
    DetailLabelList,
    DetailLabelValuePair,
    DetailValue,
} from '@strata/react-label-value-pairs';
import { ClaimStatusIndicator } from '@strata/react-status-indicators';

import { ClaimUomMap } from 'services/MedicalBillingConstants';
import { type ReferralDetails } from 'store/api/clientApi';
import { type ClaimDetails, formatCodeDescLabel } from 'store/api/medbillingApi';
import {
    useGetDiagnosisCodeDescriptionMapQuery,
    useGetEligiblePricesForOidQuery,
    useGetProductCodeMapQuery,
} from 'store/api/referenceApi';

const addQtyUnits = (qty: number | undefined, units: string | undefined) =>
    qty ? `${qty}${units ? ` ${units}` : ''}` : undefined;

interface ServiceProps {
    service: ClaimDetails['services'][0];
    index: number;
    referralDetails?: ReferralDetails;
}

// eslint-disable-next-line complexity
export const Service = ({ service, index, referralDetails = undefined }: ServiceProps) => {
    const { data: productCodeMap, isLoading: isProductCodeMapLoading } =
        useGetProductCodeMapQuery();
    const { data: diagnosisCodeDescriptionMap, isLoading: isDiagnosisCodeDescriptionMapLoading } =
        useGetDiagnosisCodeDescriptionMapQuery();

    const { data: unitPrices, isFetching: isServiceUnitPriceLoading } =
        useGetEligiblePricesForOidQuery(
            referralDetails?.referentPriceType && referralDetails?.priceSource
                ? {
                      oid: referralDetails.productServiceOid,
                      referentPriceType: referralDetails.referentPriceType,
                      isBrand: referralDetails.isBrand,
                      priceSource: referralDetails.priceSource,
                      priceDate: referralDetails.priceIsFrozen
                          ? referralDetails.referralStart
                          : service.dateOfService,
                  }
                : skipToken,
        );

    const isAWP = referralDetails?.referentPriceType === ReferentPriceTypes.AWP;
    const isASP = referralDetails?.referentPriceType === ReferentPriceTypes.ASP;

    const unitPrice = unitPrices?.find(
        (price) => price.code === (isAWP ? service.ndc : service.procedureCode),
    )?.price;

    return (
        <FormSectionWithRows
            className="margin__bottom--medium"
            sectionTitle={`Service ${index + 1}`}
            isLoading={
                isProductCodeMapLoading ||
                isDiagnosisCodeDescriptionMapLoading ||
                isServiceUnitPriceLoading
            }
            rowOneContent={
                <>
                    <DetailLabelValuePair
                        style={{ width: '33%' }}
                        label="Date(s) Of Service"
                        value={`${formatDate(service.dateOfService)} - ${formatDate(
                            service.dateOfServiceEnd,
                        )}`}
                    />
                    <DetailLabelValuePair
                        style={{ width: '33%' }}
                        label={`Procedure${conditionalString(isASP, ' (Unit Price)')}`}
                        value={`${service.procedureCodeDisplayText}${conditionalString(
                            isASP && !!unitPrice,
                            ` (${formatDollarAmount(unitPrice, true, false)})`,
                        )}`}
                    />
                    <DetailLabelValuePair
                        style={{ width: '34%' }}
                        label={`NDC${conditionalString(isAWP, ' (Unit Price)')}`}
                        value={`${formatCodeDescLabel(
                            service.ndc,
                            service.ndc && productCodeMap?.[service.ndc]?.description,
                            true,
                        )}${conditionalString(
                            isAWP && !!unitPrice,
                            ` (${formatDollarAmount(unitPrice, true, false)})`,
                        )}`}
                    />
                </>
            }
            rowTwoContent={
                <>
                    <DetailLabelValuePair
                        style={{ width: '33%' }}
                        label="Modifier Code(s)"
                        value={service.modifierCodes?.replaceAll(',', ', ')}
                    />
                    <DetailLabelValuePair
                        style={{ width: '33%' }}
                        label="Price List Source"
                        value={referralDetails?.priceSource}
                    />
                    <DetailLabelList
                        style={{ width: '34%' }}
                        label="Diagnoses"
                        values={service.diagnoses?.map((dx) =>
                            formatCodeDescLabel(dx, diagnosisCodeDescriptionMap?.[dx], true),
                        )}
                    />
                </>
            }
            rowThreeContent={
                <>
                    <DetailLabelValuePair
                        style={{ width: '33%' }}
                        label="Dispensed Qty"
                        value={addQtyUnits(
                            service.dispensedQty,
                            service.dispensedQtyUom && ClaimUomMap[service.dispensedQtyUom],
                        )}
                    />
                    <DetailLabelValuePair
                        style={{ width: '33%' }}
                        label="Billing Qty"
                        value={addQtyUnits(service.billingQty, ClaimUomMap[service.billingQtyUom])}
                    />
                    <DetailLabelValuePair
                        style={{ width: '34%' }}
                        label="Submitted Amount"
                        value={
                            <DollarAmount amount={service.submittedAmount} large showZeroValue />
                        }
                    />
                </>
            }
            rowFourContent={
                <>
                    <DetailLabelValuePair
                        style={{ width: '33%' }}
                        label="Paid Amount"
                        value={<DollarAmount amount={service.total} large showZeroValue />}
                    />
                    <div style={{ width: '33%' }}>
                        <DetailLabel label="Status" />
                        {service.serviceStatus ? (
                            <ClaimStatusIndicator status={service.serviceStatus} />
                        ) : (
                            <DetailValue />
                        )}
                    </div>
                    <DetailLabelList
                        style={{ width: '34%' }}
                        label="Claim Status Codes"
                        values={service?.serviceStatusCodes}
                    />
                </>
            }
            rowFiveContent={
                <DetailLabelList
                    style={{ width: '33%' }}
                    label="Secondary Claim Status Codes"
                    values={service?.secondaryServiceStatusCodes}
                />
            }
        />
    );
};
